import { Drag } from "assets/icons/Common";
import { DashboardContext } from "contexts/DashboardContext";
import { Identifier } from "dnd-core";
import { FC, useContext, useRef } from "react";
import { useDrag, useDrop } from "react-dnd";
import { useHistory } from "react-router-dom";
import styled from "styled-components";
import { DashboardProps } from "types/GlobalData";

import { DashboardActions } from "./DashboardActions";

interface Props {
  dashboard: DashboardProps;
  backgroundUrl: string;
  groupId: string;
  handleDashboardNameChange: (id: string, name: string) => void;
  handleDuplicateDashboard: (id: string) => void;
  handleDeleteDashboard: (id: string) => void;
  onUpdateOrder: (draggedId: string, targetId: string) => void;
}

const ITEM_NAME = "dashboard-item";

interface CollectedProps {
  handlerId: Identifier | null;
  isOver: boolean;
}

interface DragItem {
  id: string;
  type: string;
}

export const DashboardItem: FC<Props> = ({
  dashboard,
  backgroundUrl,
  groupId,
  handleDashboardNameChange,
  handleDuplicateDashboard,
  handleDeleteDashboard,
  onUpdateOrder,
}) => {
  const { data: globalData, setData: updateGlobalData } = useContext(DashboardContext);
  const dndRef = useRef<HTMLDivElement>(null);
  const history = useHistory();

  const [{ handlerId, isOver }, drop] = useDrop<DragItem, void, CollectedProps>({
    accept: ITEM_NAME,
    drop(item) {
      if (item.id === dashboard.id) {
        return;
      }

      onUpdateOrder(item.id, dashboard.id);
    },
    collect: (monitor) => ({
      handlerId: monitor.getHandlerId(),
      isOver: monitor.isOver({ shallow: true }),
    }),
  });

  const [{ isDragging }, drag] = useDrag<DragItem, void, { isDragging: boolean }>({
    type: ITEM_NAME,
    item: () => ({ id: dashboard.id, type: ITEM_NAME }),
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const handlePreventDnd = (
    event: React.MouseEvent<HTMLButtonElement> | React.MouseEvent<HTMLDivElement>,
  ) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const dragStyle = {
    opacity: isDragging ? 0.5 : 1,
    outline: isOver && !isDragging ? "2px solid var(--widget-primary)" : undefined,
  };
  drag(drop(dndRef));

  const handleDashboardSlideClick = () => {
    updateGlobalData({ activeDashboardId: dashboard.id, activeDashboardGroupId: groupId });
    history.push("/");
  };

  return (
    <ItemWrap key={dashboard.id} ref={dndRef} data-handler-id={handlerId} style={dragStyle}>
      <DashboardImage backgroundUrl={backgroundUrl} onClick={handleDashboardSlideClick} />
      <DashboardActions
        name={dashboard.name}
        id={dashboard.id}
        nameChanged={handleDashboardNameChange}
        duplicateDashboard={handleDuplicateDashboard}
        deleteDashboard={handleDeleteDashboard}
        groupId={groupId}
      />
      <DragIcon />
    </ItemWrap>
  );
};

const DragIcon = styled(Drag)`
  position: absolute;
  top: 16px;
  left: 50%;
  transform: translate(-50%, 0);
  width: 26px;
  height: 26px;
  color: #fff;
  display: none;
  pointer-events: none;
`;

const ItemWrap = styled.div`
  width: calc(20% - 12px);
  background: transparent;
  border-radius: 8px;
  position: relative;

  &:hover {
    ${DragIcon} {
      display: block;
    }
  }
`;

const DashboardImage = styled.div.attrs({ className: "no-drag" })<{
  backgroundUrl: string;
}>`
  border-radius: 8px;
  height: 100px;
  width: 100%;
  background-color: ${(props) => props.color};
  background-color: var(--widget-primary);
  background-image: url(${(props) => props.backgroundUrl});
  cursor: pointer;
  transition: transform 0.1s ease;

  &:hover {
    transform: scale(1.02);
  }
`;
